import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { MetaTags } from "@/components/MetaTags";

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  image?: string;
}

export function Layout({ children, title, description, image }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuLinks = [
    { name: "Mint NFT", path: "/" },
    { name: "Rarity Report", path: "/report", internal: true },
    { name: "Documentation", path: "/documentation" },
    { name: "FAQ", path: "/faq" },
    { name: "Support", path: "/support" },
    { name: "License", path: "/license" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Cookie Policy", path: "/cookie-policy" }
  ];

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <MetaTags title={title} description={description} image={image} />
      {/* Fixed Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link 
            to="/"
            className="text-lg md:text-2xl font-pixel text-[#9333EA] hover:opacity-90 transition-opacity cursor-pointer"
          >
            Cryptfluencers
          </Link>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              variant="ghost"
            >
              <div className="space-y-1.5">
                <div className="w-5 h-0.5 bg-current"></div>
                <div className="w-5 h-0.5 bg-current"></div>
                <div className="w-5 h-0.5 bg-current"></div>
              </div>
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute top-full left-0 w-full bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="container mx-auto py-4 px-4">
              <div className="grid grid-cols-1 gap-2">
                {menuLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-sm text-white/80 hover:text-[#9333EA] hover:bg-white/10 px-4 py-2 rounded-lg transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="h-px bg-white/10 my-2" />
                <div className="px-4 py-2">
                  <p className="text-sm text-white/60 mb-2">Follow Us</p>
                  <a
                    href="https://x.com/cryptfluencers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 rounded-lg transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                    <span>Twitter</span>
                    <span className="text-xs text-white/60">↗</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Background Grid */}
      <div className="fixed inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
      
      {/* Main Content */}
      <div className="flex-grow container mx-auto px-4 relative z-10 pt-24 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto"
        >
          {children}
        </motion.div>
      </div>

      {/* Scroll To Top Button */}
      <ScrollToTop />
    </div>
  );
} 